import React from 'react'
import clsx from 'clsx'
import { KTSVG } from '../../../../../../../_metronic/helpers'
import { LoadingSpan } from '../../../../../components/Loadings/loadingSpan'

export type PlanUsage = {
  plano: 'PREMIUM' | 'STARTER' | 'BASICA'
  limit: number
  imported: number
}

export const PLAN_LABELS: Record<string, string> = {
  PREMIUM: 'PREMIUM',
  STARTER: 'STARTER',
  BASICA: 'BÁSICA',
}

// cor do tema de cada plano (BASICA usa a cor da empresa)
export const PLAN_COLORS: Record<string, string> = {
  PREMIUM: 'success',
  STARTER: 'info',
  BASICA: 'primary',
}

export const PLAN_BADGES: Record<string, string> = {
  PREMIUM: `badge-light-${PLAN_COLORS.PREMIUM}`,
  STARTER: `badge-light-${PLAN_COLORS.STARTER}`,
  BASICA: `badge-light-${PLAN_COLORS.BASICA}`,
}

export const isPlanExceeded = (plan: PlanUsage | undefined, newSelected: number) =>
  !!plan && newSelected > 0 && (plan.imported + newSelected) > plan.limit

type CardsProps = {
  planUsage: PlanUsage[]
  loading: boolean
  typePlano: string
  newSelected: number
  disabled: boolean
  onSelectPlano: (plano: string) => void
}

const PlanUsageCards: React.FC<CardsProps> = ({
  planUsage,
  loading,
  typePlano,
  newSelected,
  disabled,
  onSelectPlano
}) => {
  if (loading) {
    return (
      <div className='text-muted fw-semibold fs-7'>
        Carregando planos... <LoadingSpan style={{marginLeft: '0.5rem'}}/>
      </div>
    )
  }

  if (planUsage.length === 0) {
    return null
  }

  return (
    <div style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
      {planUsage.map((plan) => {
        const isSelected = plan.plano === typePlano
        const isEmpty = plan.limit <= 0
        const used = plan.imported + (isSelected ? newSelected : 0)
        const isOver = isSelected && isPlanExceeded(plan, newSelected)
        const percent = plan.limit > 0 ? Math.min((used / plan.limit) * 100, 100) : 0
        const color = isOver ? 'danger' : PLAN_COLORS[plan.plano]

        return (
          <div
            key={plan.plano}
            className={clsx(
              'card border border-dashed px-6 py-4',
              isSelected || isOver ? `border-${color} bg-light-${color}` : 'border-gray-300'
            )}
            style={{
              flex: '1 1 220px',
              opacity: isEmpty ? 0.5 : 1,
              cursor: isEmpty || disabled ? 'not-allowed' : 'pointer',
            }}
            title={isEmpty ? 'Sua empresa não possui usinas neste plano' : `Importar no plano ${PLAN_LABELS[plan.plano]}`}
            onClick={() => {
              if (!isEmpty && !disabled) {
                onSelectPlano(plan.plano)
              }
            }}
          >
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <span className={clsx('fw-bold fs-6', isEmpty ? 'text-gray-500' : `text-${color}`)}>
                  {PLAN_LABELS[plan.plano]}
                </span>
                {isSelected && !isEmpty && (
                  <span className={`badge badge-${color} fs-9`}>Selecionado</span>
                )}
              </div>
              {isEmpty ? (
                <span className='text-gray-500 fs-7'>Sem usinas no plano</span>
              ) : (
                <span className='fs-7'>
                  <span className={clsx('fw-bolder', isOver ? 'text-danger' : 'text-gray-800')}>{used}</span>
                  <span className='text-muted'> / {plan.limit} usinas</span>
                </span>
              )}
            </div>

            <div className='h-6px w-100 bg-gray-300 rounded mt-3'>
              <div
                className={`rounded h-6px bg-${color}`}
                role='progressbar'
                style={{width: `${percent}%`}}
              ></div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

type WarningProps = {
  plan: PlanUsage | undefined
  newSelected: number
}

const PlanUsageWarning: React.FC<WarningProps> = ({plan, newSelected}) => {
  if (!plan || !isPlanExceeded(plan, newSelected)) {
    return null
  }

  const available = Math.max(plan.limit - plan.imported, 0)

  return (
    <div className='notice d-flex align-items-center bg-light-danger rounded border-danger border border-dashed p-4'>
      <KTSVG
        path='/media/icons/duotune/general/gen044.svg'
        className='svg-icon-2 svg-icon-danger me-3'
      />
      <div className='fs-7 text-danger fw-semibold'>
        A quantidade de usinas selecionadas excede o plano {PLAN_LABELS[plan.plano]}
        {' '}({available} disponíve{available === 1 ? 'l' : 'is'}).
        {' '}Remova {plan.imported + newSelected - plan.limit} usina(s) selecionada(s) ou escolha outro plano.
      </div>
    </div>
  )
}

export {PlanUsageCards, PlanUsageWarning}
